import { defineStore } from 'pinia'
import { ref } from 'vue'
import axiosInstance from '@/shared/utils/axios';
import { Branch, Equipment, Customer, Installation, AssignedTask, Checklist, Service } from '@/types/types';
import axios from 'axios';
import { useAuthStore } from '@/core/auth/auth';
import { can } from '@/core/permissions/can';
import { skipPerm } from '@/core/permissions/skipPerm';
import { CustomerMessages } from '@/types/types';
import { Invoice, InvoicePaymentParams } from '@/types/invoice';

export interface BranchesData {
  branches: Branch[];
  totalCount: number;
  page: number;
  limit: number;
}

export interface EquipmentData {
  equipment: Equipment[];
  totalCount: number;
  page: number;
  limit: number;
}

export interface CustomersData {
  customers: Customer[];
  totalCount: number;
  page: number;
  limit: number;
  totalPages: number;
  filterCounts: {
    custType: { [key: string]: number };
  };
}

export interface InstallationsData {
  installations: Installation[];
  totalCount: number;
  page: number;
  limit: number;
  totalPages: number;
  filterCounts: {
    type: { [key: string]: number };
    status: { [key: string]: number };
  };
}

export interface AssignedTasksData {
  assignedTasks: AssignedTask[];
  totalCount: number;
  page: number;
  limit: number;
  totalPages: number;
  filterCounts: {
    status: { [key: string]: number };
  };
}

export interface ChecklistData {
  items: Checklist[];
  totalCount: number;
  page: number;
  limit: number;
  totalPages: number;
  filterCounts: {
    status: { [key: string]: number };
    type: { [key: string]: number };
    location: { [key: string]: number };
    center?: { [key: string]: number };
    visp?: { [key: string]: number };
  };
}

export interface ServicesData {
  services: Service[];
  totalCount: number;
  page: number;
  limit: number;
  isLoading: boolean;
  hasMore: boolean;
}

interface InvoicesData {
  invoices: Invoice[];
  summary: {
    totalAmount: number;
    totalBalance: number;
    totalInvoices: number;
    paidInvoices: number;
    unpaidInvoices: number;
  };
  totalCount: number;
}

interface DepositAccount {
  id: string;
  name: string;
  accountType: string;
  accountSubType: string;
  currentBalance: number;
  active: boolean;
  classification: string;
  fullyQualifiedName: string;
}

interface FetchChecklistParams {
  page: number;
  limit: number;
  status: string;
  type: string;
  date?: string;
  [key: string]: string | number | undefined;
}

interface PaymentsData {
  payments: Array<{
    id: string;
    date: string;
    customer: string;
    amount: number;
    status: string;
    referenceNo?: string;
  }>;
  totalCount: number;
  page: number;
  limit: number;
  totalPages: number;
}

// New interface for Transactions
interface TransactionsData {
  invoices: Array<any>;
  payments: Array<any>;
  transactions?: Array<any>;
  summary: {
    totalAmount: number;
    totalBalance: number;
    totalInvoices: number;
    paidInvoices: number;
    unpaidInvoices: number;
    totalPayments: number;
    totalPaymentAmount: number;
  };
  pagination: {
    page: number;
    limit: number;
  };
  success: boolean;
}

export interface State {
  branchesData: BranchesData;
  equipmentData: EquipmentData;
  customersData: CustomersData;
  installationsData: InstallationsData;
  assignedTasksData: AssignedTasksData;
  checklistData: ChecklistData;
  servicesData: ServicesData;
  invoicesData: InvoicesData;
  paymentsData: PaymentsData;
  transactionsData: TransactionsData;
  token: string | null;
}

export const useMainStore = defineStore('main', () => {
  const branchesData = ref<BranchesData>({
    branches: [],
    totalCount: 0,
    page: 1,
    limit: 10
  });

  const equipmentData = ref<EquipmentData>({
    equipment: [],
    totalCount: 0,
    page: 1,
    limit: 10
  });

  const customersData = ref<CustomersData>({
    customers: [],
    totalCount: 0,
    page: 1,
    limit: 10,
    totalPages: 0,
    filterCounts: {
      custType: {}
    }
  });

  const installationsData = ref<InstallationsData>({
    installations: [],
    totalCount: 0,
    page: 1,
    limit: 10,
    totalPages: 0,
    filterCounts: {
      type: {},
      status: {}
    }
  });

  const assignedTasksData = ref<AssignedTasksData>({
    assignedTasks: [],
    totalCount: 0,
    page: 1,
    limit: 10,
    totalPages: 0,
    filterCounts: {
      status: {}
    }
  });

  const checklistData = ref<ChecklistData>({
    items: [],
    totalCount: 0,
    page: 1,
    limit: 10,
    totalPages: 0,
    filterCounts: {
      status: {},
      type: {},
      location: {}
    }
  });

  const servicesData = ref<ServicesData>({
    services: [],
    totalCount: 0,
    page: 1,
    limit: 200,
    isLoading: false,
    hasMore: true,
  });

  const invoicesData = ref<InvoicesData>({
    invoices: [],
    summary: {
      totalAmount: 0,
      totalBalance: 0,
      totalInvoices: 0,
      paidInvoices: 0,
      unpaidInvoices: 0
    },
    totalCount: 0
  });

  const paymentsData = ref<PaymentsData>({
    payments: [],
    totalCount: 0,
    page: 1,
    limit: 10,
    totalPages: 0
  });

  const transactionsData = ref<TransactionsData>({
    invoices: [],
    payments: [],
    transactions: [],
    summary: {
      totalAmount: 0,
      totalBalance: 0,
      totalInvoices: 0,
      paidInvoices: 0,
      unpaidInvoices: 0,
      totalPayments: 0,
      totalPaymentAmount: 0
    },
    pagination: {
      page: 1,
      limit: 10
    },
    success: false
  });

  const token = ref<string | null>(null);

  const DEPOSIT_ACCOUNTS_STORAGE_KEY = 'fcrm_qbo_deposit_accounts';

  const loadCachedDepositAccounts = (): DepositAccount[] => {
    if (typeof window === 'undefined') return [];
    try {
      const raw = localStorage.getItem(DEPOSIT_ACCOUNTS_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed as DepositAccount[];
        }
      }
    } catch (e) {
      console.warn('Failed to parse cached deposit accounts from localStorage:', e);
    }
    return [];
  };

  const initialDepositAccounts = loadCachedDepositAccounts();
  const depositAccounts = ref<DepositAccount[]>(initialDepositAccounts);
  const depositAccountsLoading = ref(false);
  const depositAccountsLoaded = ref(initialDepositAccounts.length > 0);

  // Global QB data (classes, locations, centers, visps)
  const qboClasses = ref<any[]>([]);
  const qboLocations = ref<any[]>([]);
  const qboCenters = ref<any[]>([]);
  const qboVisps = ref<any[]>([]);
  const qboSupporters = ref<any[]>([]);
  const qboStatuses = ref<any[]>([]);
  const qboCurrencies = ref<any[]>([]);
  const qboPaymentMethods = ref<any[]>([]);
  const qboPaymentTerms = ref<any[]>([]);
  const qboDeliveryOptions = ref<any[]>([]);
  const qboLanguages = ref<any[]>([]);
  const qboItems = ref<any[]>([]);
  const qboItemsLoading = ref(false);
  const qboItemsLoaded = ref(false);
  const qboDataLoading = ref(false);
  const qboDataLoaded = ref(false);
  /** Which SIB tag the cached centers/visps/etc were loaded for (null = user default) */
  const qboDataRadiusServerTag = ref<string | null>(null);
  const resellers = ref<any[]>([]);
  const resellersLoading = ref(false);
  const resellersData = ref({
    resellers: [] as any[],
    totalCount: 0,
    page: 1,
    limit: 10
  });
  // Function to clear QB data cache
  const clearQboDataCache = () => {
    qboClasses.value = [];
    qboLocations.value = [];
    qboCenters.value = [];
    qboVisps.value = [];
    qboSupporters.value = [];
    qboStatuses.value = [];
    qboCurrencies.value = [];
    qboPaymentMethods.value = [];
    qboPaymentTerms.value = [];
    qboDeliveryOptions.value = [];
    qboLanguages.value = [];
    qboItems.value = [];
    qboItemsLoaded.value = false;
    qboItemsLoading.value = false;
    qboDataLoaded.value = false;
    qboDataLoading.value = false;
    qboDataRadiusServerTag.value = null;
    qboConfigLoaded.value = false;
    qboConfigLoading.value = false;
  };

  const fetchQboItems = async () => {
    if (qboItemsLoaded.value) return;
    if (qboItemsLoading.value) {
      while (qboItemsLoading.value) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
      return;
    }
    try {
      qboItemsLoading.value = true;
      const response = await axiosInstance.get('/api/qbo/config/items', skipPerm);
      qboItems.value = response.data?.items || [];
      qboItemsLoaded.value = true;
    } catch (error: any) {
      console.error('Error fetching QBO items:', error?.message || error);
    } finally {
      qboItemsLoading.value = false;
    }
  };

  /** QB payment/currency config — only needed when opening QB registration drawer */
  const fetchQboConfigData = async () => {
    if (qboConfigLoaded.value) return;
    if (qboConfigLoading.value) {
      while (qboConfigLoading.value) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
      return;
    }
    try {
      qboConfigLoading.value = true;
      const [currenciesRes, paymentMethodsRes, paymentTermsRes, deliveryOptionsRes, languagesRes] =
        await Promise.all([
          axiosInstance.get('/api/qbo/config/currencies', skipPerm),
          axiosInstance.get('/api/qbo/config/payment-methods', skipPerm),
          axiosInstance.get('/api/qbo/config/payment-terms', skipPerm),
          axiosInstance.get('/api/qbo/config/delivery-options', skipPerm),
          axiosInstance.get('/api/qbo/config/languages', skipPerm),
        ]);

      qboCurrencies.value = currenciesRes.data?.currencies || [];

      const paymentMethodsData = paymentMethodsRes.data?.paymentMethods || [];
      const flattenedPaymentMethods: {
        id: string;
        name: string;
        type?: string;
        isActive?: boolean;
        isCategory?: boolean;
        parentCategory?: string;
      }[] = [];

      paymentMethodsData.forEach((item: any) => {
        if (item.type === 'category' && item.accounts) {
          flattenedPaymentMethods.push({
            id: item.id,
            name: item.name,
            type: 'category',
            isCategory: true,
          });
          item.accounts.forEach((account: any) => {
            flattenedPaymentMethods.push({
              id: account.id,
              name: account.name,
              type: account.type,
              isActive: account.isActive,
              parentCategory: item.id,
            });
          });
        } else if (item.type === 'account') {
          flattenedPaymentMethods.push({
            id: item.id,
            name: item.name,
            type: 'account',
            isActive: item.isActive,
          });
        }
      });

      qboPaymentMethods.value = flattenedPaymentMethods;
      qboPaymentTerms.value = paymentTermsRes.data?.terms || [];
      qboDeliveryOptions.value = deliveryOptionsRes.data?.deliveryOptions || [];
      qboLanguages.value = languagesRes.data?.languages || [];
      qboConfigLoaded.value = true;
    } catch (error: any) {
      console.error('Error fetching QBO config:', error?.message || error);
    } finally {
      qboConfigLoading.value = false;
    }
  };

  const makeSecureRequest = async (url: string, method: string, data: any = null) => {
    try {
      const config = {
        method,
        url,
        data,
        headers: {
          'Content-Type': 'application/json'
        }
      };

      const response = await axios(config);
      return response.data;
    } catch (error) {
      console.error('Error making secure request:', error);
      throw error;
    }
  };

  const fetchBranches = async (params = {}) => {
    try {
      const authStore = useAuthStore();
      const response = await axiosInstance.get('/api/branches', {
        params: {
          ...params,
          _t: Date.now() // Cache buster
        },
        headers: {
          'Authorization': `Bearer ${authStore.token}`
        }
      });

      branchesData.value = {
        branches: response.data?.branches || [],
        totalCount: response.data?.pagination?.totalCount ?? 0,
        page: response.data?.pagination?.page ?? 1,
        limit: response.data?.pagination?.limit ?? 10
      };

      return response.data;
    } catch (error) {
      console.error('Error fetching branches:', error);
      throw error;
    }
  };

  const addBranch = async (branchData: Partial<Branch>) => {
    try {
      const response = await axiosInstance.post('/api/branches', branchData);
      const { branch: newBranch, branches, pagination } = response.data;

      // Update store with the returned list and pagination
      branchesData.value = {
        branches,
        totalCount: pagination.totalCount,
        page: pagination.page,
        limit: pagination.limit
      };

      return newBranch;
    } catch (error) {
      console.error('Error adding branch:', error);
      throw error;
    }
  };

  const updateBranch = async (branchData: Branch) => {
    try {
      if (!branchData || !branchData._id) {
        throw new Error('Invalid branch data: Missing _id');
      }

      const response = await axiosInstance.put(`/api/branches/${branchData._id}`, branchData);

      const { branch: updatedBranch, branches, pagination } = response.data;

      if (!updatedBranch || !updatedBranch._id) {
        throw new Error('Invalid response: Updated branch data is missing or incomplete');
      }

      // Update store with the returned list and pagination
      branchesData.value = {
        branches,
        totalCount: pagination.totalCount,
        page: pagination.page,
        limit: pagination.limit
      };

      return updatedBranch;
    } catch (error) {
      if (error instanceof Error) {
        console.error('Error stack:', error.stack);
      }
      throw error;
    }
  };

  const getBranchById = async (id: string) => {
    try {
      const response = await axiosInstance.get(`/api/branches/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching branch by ID:', error);
      throw error;
    }
  };

  const deleteBranch = async (id: string) => {
    try {
      await axiosInstance.delete(`/api/branches/${id}`);
      branchesData.value.branches = branchesData.value.branches.filter(branch => branch._id !== id);
      branchesData.value.totalCount--;
    } catch (error) {
      console.error('Error deleting branch:', error);
      throw error;
    }
  };

  const fetchEquipment = async (page: number = 1, limit: number = 10, search: string = "", type: string = "") => {
    try {
      const response = await axiosInstance.get('/api/equipment', {
        params: {
          page,
          limit,
          search,
          ...(type ? { type } : {})
        },
        ...skipPerm
      });
      equipmentData.value = {
        equipment: response.data.equipment,
        totalCount: response.data.pagination.totalCount,
        page: response.data.pagination.page,
        limit: response.data.pagination.limit
      };
    } catch (error) {
      console.error('Error fetching equipment:', error);
      throw error;
    }
  };

  const addEquipment = async (newEquipmentData: Partial<Equipment>) => {
    try {
      const response = await axiosInstance.post('/api/equipment', newEquipmentData, {
        params: {
          page: equipmentData.value.page,
          limit: equipmentData.value.limit
        }
      });
      const { equipment: newEquipment, equipmentList, pagination } = response.data;

      // Update store with the returned list and pagination
      equipmentData.value = {
        equipment: equipmentList,
        totalCount: pagination.totalCount,
        page: pagination.page,
        limit: pagination.limit
      };

      return newEquipment;
    } catch (error) {
      console.error('Error adding equipment:', error);
      throw error;
    }
  };

  const updateEquipment = async (equipmentToUpdate: Equipment) => {
    try {
      if (!equipmentToUpdate || !equipmentToUpdate._id) {
        throw new Error('Invalid equipment data: Missing _id');
      }

      const response = await axiosInstance.put(`/api/equipment/${equipmentToUpdate._id}`, equipmentToUpdate, {
        params: {
          page: equipmentData.value.page,
          limit: equipmentData.value.limit
        }
      });

      const { equipment: updatedEquipment, equipmentList, pagination } = response.data;

      if (!updatedEquipment || !updatedEquipment._id) {
        throw new Error('Invalid response: Updated equipment data is missing or incomplete');
      }

      // Update store with the returned list and pagination
      equipmentData.value = {
        equipment: equipmentList,
        totalCount: pagination.totalCount,
        page: pagination.page,
        limit: pagination.limit
      };

      return updatedEquipment;
    } catch (error) {
      if (error instanceof Error) {
        console.error('Error stack:', error.stack);
      }
      throw error;
    }
  };

  const getEquipmentById = async (id: string) => {
    try {
      const response = await axiosInstance.get(`/api/equipment/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching equipment by ID:', error);
      throw error;
    }
  };

  const deleteEquipment = async (id: string) => {
    try {
      const response = await axiosInstance.delete(`/api/equipment/${id}`, {
        params: {
          page: equipmentData.value.page,
          limit: equipmentData.value.limit
        }
      });

      const { equipmentList, pagination } = response.data;

      // Update store with the returned list and pagination
      equipmentData.value = {
        equipment: equipmentList,
        totalCount: pagination.totalCount,
        page: pagination.page,
        limit: pagination.limit
      };
    } catch (error) {
      console.error('Error deleting equipment:', error);
      throw error;
    }
  };

  const fetchCustomers = async (page: number = 1, limit: number = 10, search: string = "", custType: string = "") => {
    try {
      const response = await axiosInstance.get('/api/customers', {
        params: {
          page,
          limit,
          search,
          custType
        }
      });
      customersData.value = {
        customers: response.data.customers,
        totalCount: response.data.totalCount,
        page: response.data.page,
        limit: response.data.limit,
        totalPages: response.data.totalPages,
        filterCounts: response.data.filterCounts
      };
    } catch (error) {
      console.error('Error fetching customers:', error);
      throw error;
    }
  };

  const addCustomer = async (newCustomerData: Partial<Customer>) => {
    try {
      const response = await axiosInstance.post('/api/customers', newCustomerData);
      const { customer: newCustomer, installation, message, customerLink } = response.data;
      const customerLinkId = customerLink?._id || null;
      const customerWithLink = customerLinkId
        ? { ...newCustomer, customerLinkId }
        : newCustomer;

      if (customersData.value.customers) {
        customersData.value.customers.push(customerWithLink);
      }
      if (customersData.value.totalCount !== undefined) {
        customersData.value.totalCount++;
      }
      return {
        customer: customerWithLink,
        installation,
        // Needed for contract print QR (feedback URL uses QboCustomerLink _id)
        customerLink: customerLink || null,
        message: message || CustomerMessages.CREATE_SUCCESS(newCustomer.name)
      };
    } catch (error: any) {
      console.error('Error adding customer:', error);
      // The axios interceptor returns error.response.data directly
      const errorMessage = error.message || error.error ||
        CustomerMessages.CREATE_ERROR(newCustomerData.name || 'Customer');
      throw new Error(errorMessage);
    }
  };

  const updateCustomer = async (customerData: Customer) => {
    try {
      if (!customerData || !customerData._id) {
        throw new Error('Invalid customer data: Missing _id');
      }

      const response = await axiosInstance.put(`/api/customers/${customerData._id}`, customerData);
      const { customer: updatedCustomer, message, customerLink, customerLinkId } = response.data;

      if (!updatedCustomer || !updatedCustomer._id) {
        throw new Error('Invalid response: Updated customer data is missing or incomplete');
      }

      const resolvedLinkId = customerLink?._id || customerLinkId || null;
      const customerWithLink = resolvedLinkId
        ? { ...updatedCustomer, customerLinkId: resolvedLinkId }
        : updatedCustomer;

      customersData.value.customers = customersData.value.customers.map(customer =>
        customer._id === customerWithLink._id ? customerWithLink : customer
      );

      return {
        customer: customerWithLink,
        customerLink: customerLink || (resolvedLinkId ? { _id: resolvedLinkId } : null),
        message: message || CustomerMessages.UPDATE_SUCCESS(updatedCustomer.name)
      };
    } catch (error: any) {
      console.error('Error updating customer:', error);
      const errorMessage = error.response?.data?.message ||
        CustomerMessages.UPDATE_ERROR(customerData.name);
      throw new Error(errorMessage);
    }
  };

  const getCustomerById = async (id: string) => {
    try {
      const response = await axiosInstance.get(`/api/customers/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching customer by ID:', error);
      throw error;
    }
  };

  const deleteCustomer = async (id: string) => {
    try {
      const customer = customersData.value.customers.find(c => c._id === id);
      const response = await axiosInstance.delete(`/api/customers/${id}`);
      customersData.value.customers = customersData.value.customers.filter(c => c._id !== id);
      customersData.value.totalCount--;
      return {
        message: response.data.message || CustomerMessages.DELETE_SUCCESS(customer?.name || 'Customer')
      };
    } catch (error: any) {
      console.error('Error deleting customer:', error);
      const customer = customersData.value.customers.find(c => c._id === id);
      const errorMessage = error.response?.data?.message ||
        CustomerMessages.DELETE_ERROR(customer?.name || 'Customer');
      throw new Error(errorMessage);
    }
  };

  const updateCustomerDevices = async (data: { customerId: string; devices: any[] }) => {
    try {
      const authStore = useAuthStore();
      if (!authStore.isAuthenticated) {
        throw new Error('User is not authenticated');
      }

      const response = await axiosInstance.put(`/api/customers/${data.customerId}/devices`, data, {
        headers: {
          'Authorization': `Bearer ${authStore.token}`
        }
      });

      const { customer: updatedCustomer, message } = response.data;

      if (!updatedCustomer || !updatedCustomer._id) {
        throw new Error('Invalid response: Updated customer data is missing or incomplete');
      }

      customersData.value.customers = customersData.value.customers.map(customer =>
        customer._id === updatedCustomer._id ? updatedCustomer : customer
      );

      return {
        customer: updatedCustomer,
        message: message || CustomerMessages.DEVICE_UPDATE_SUCCESS(updatedCustomer.name)
      };
    } catch (error) {
      if (error instanceof Error) {
        console.error('Error updating customer devices:', error.stack);
      }
      throw error;
    }
  };


  const fetchLastContractId = async (
    customerStatus: 'demo' | 'final' = 'final',
    options?: {
      branchId?: string | null
      rs_Id?: string | null
      radiusServerTag?: string | null
    }
  ) => {
    try {
      const authStore = useAuthStore();
      const hasOption = Boolean(options?.branchId || options?.rs_Id || options?.radiusServerTag);
      const response = await axiosInstance.get('/api/customers/last-contract-id', {
        params: {
          customerStatus,
          ...(options?.branchId ? { branch_id: options.branchId } : {}),
          ...(options?.rs_Id
            ? { rs_Id: options.rs_Id }
            : !hasOption && authStore.user?.rs_Id
            ? { rs_Id: authStore.user.rs_Id }
            : {}),
          ...(options?.radiusServerTag
            ? { radiusServerTag: options.radiusServerTag }
            : !hasOption && authStore.user?.radiusServerTag
            ? { radiusServerTag: authStore.user.radiusServerTag }
            : {}),
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching last contract ID:', error);
      throw error;
    }
  };
  const translateCustomerName = async (nameFa: string, lastNameFa: string, signal?: AbortSignal) => {
    try {
      const response = await axiosInstance.post('/api/customers/translate-name', { nameFa, lastNameFa }, { signal });
      return response.data;
    } catch (error) {
      if (axios.isCancel(error)) {
        console.log('Request canceled', error.message);
      } else {
        console.error('Error translating customer name:', error);
      }
      throw error;
    }
  };

  const fetchQboCustomFields = async () => {
    try {
      const response = await axiosInstance.get('/api/customers/qbo-custom-fields');
      return response.data;
    } catch (error) {
      console.error('Error fetching QBO custom fields:', error);
      throw error;
    }
  };
  const fetchResellers = async (params: { page?: number; limit?: number; search?: string; branch_id?: string } | string = "", branchId: string = "") => {
    try {
      resellersLoading.value = true;
      let queryParams: any = {};
      if (typeof params === 'object' && params !== null) {
        queryParams = { ...params };
      } else {
        queryParams = {
          search: params,
          branch_id: branchId || undefined
        };
      }

      const response = await axiosInstance.get('/api/resellers', {
        ...skipPerm,
        params: {
          ...queryParams,
          _t: Date.now()
        }
      });

      const list = response.data.resellers || [];
      resellers.value = list;

      resellersData.value = {
        resellers: list,
        totalCount: response.data.totalCount !== undefined ? response.data.totalCount : list.length,
        page: response.data.page || 1,
        limit: response.data.limit || 10
      };

      return response.data;
    } catch (error) {
      console.error('Error fetching resellers:', error);
      throw error;
    } finally {
      resellersLoading.value = false;
    }
  };
  const addReseller = async (resellerData: { name: string; branch_id?: string | null }) => {
    try {
      const response = await axiosInstance.post('/api/resellers', resellerData);
      const newReseller = response.data.reseller;
      resellers.value.unshift(newReseller);
      return newReseller;
    } catch (error: any) {
      console.error('Error adding reseller:', error);
      throw new Error(error.response?.data?.message || error.message || 'Failed to create reseller');
    }
  };
  const updateReseller = async (id: string, resellerData: { name?: string; branch_id?: string | null }) => {
    try {
      const response = await axiosInstance.put(`/api/resellers/${id}`, resellerData);
      const updated = response.data.reseller;
      const index = resellers.value.findIndex((r: any) => r._id === id);
      if (index !== -1) {
        resellers.value[index] = updated;
      }
      return updated;
    } catch (error: any) {
      console.error('Error updating reseller:', error);
      throw new Error(error.response?.data?.message || error.message || 'Failed to update reseller');
    }
  };
  const deleteReseller = async (id: string) => {
    try {
      await axiosInstance.delete(`/api/resellers/${id}`);
      resellers.value = resellers.value.filter((r: any) => r._id !== id);
    } catch (error: any) {
      console.error('Error deleting reseller:', error);
      throw new Error(error.response?.data?.message || error.message || 'Failed to delete reseller');
    }
  };

  const fetchInstallations = async (
    page: number = 1,
    limit: number = 10,
    type: string = "",
    status: string = "",
    installer: string = "",
    search: string = "",
    rs_Id: string = "",
    branch_id: string = "",
    centerId: string = "",
    startDate: string = "",
    endDate: string = ""
  ) => {
    try {
      const params: Record<string, string | number> = {
        page,
        limit,
        type,
        status,
        installer,
        search,
      };
      if (rs_Id) params.rs_Id = rs_Id;
      if (branch_id) params.branch_id = branch_id;
      if (centerId) params.centerId = centerId;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const response = await axiosInstance.get('/api/installations', { params });
      installationsData.value = {
        installations: response.data.installations,
        totalCount: response.data.totalCount,
        page: response.data.page,
        limit: response.data.limit,
        totalPages: response.data.totalPages,
        filterCounts: response.data.filterCounts
      };
    } catch (error) {
      console.error('Error fetching installations:', error);
      throw error;
    }
  };

  const addInstallation = async (newInstallationData: Partial<Installation>) => {
    try {
      const authStore = useAuthStore();
      if (!authStore.isAuthenticated) {
        throw new Error('User is not authenticated');
      }

      const response = await axiosInstance.post('/api/installations', newInstallationData, {
        headers: {
          'Authorization': `Bearer ${authStore.token}`
        }
      });
      const newInstallation = response.data.newInstallation;
      if (installationsData.value.installations) {
        installationsData.value.installations.push(newInstallation);
      }
      if (installationsData.value.totalCount !== undefined) {
        installationsData.value.totalCount++;
      }
      return newInstallation;
    } catch (error) {
      console.error('Error adding installation:', error);
      throw error;
    }
  };

  const updateInstallation = async (installationData: any) => {
    try {
      if (!installationData || !installationData._id) {
        throw new Error('Invalid installation data: Missing _id');
      }

      const response = await axiosInstance.put(`/api/installations/${installationData._id}`, installationData);
      const updatedInstallation = response.data;

      if (!updatedInstallation || !updatedInstallation._id) {
        throw new Error('Invalid response: Updated installation data is missing or incomplete');
      }

      installationsData.value.installations = installationsData.value.installations.map((installation: Installation) =>
        installation._id === updatedInstallation._id ? updatedInstallation : installation
      );

      return updatedInstallation;
    } catch (error) {
      if (error instanceof Error) {
        console.error('Error stack:', error.stack);
      }
      throw error;
    }
  };

  const deleteInstallation = async (id: string) => {
    try {
      await axiosInstance.delete(`/api/installations/${id}`);
      installationsData.value.installations = installationsData.value.installations.filter((installation: Installation) => installation._id !== id);
      installationsData.value.totalCount--;
    } catch (error) {
      console.error('Error deleting installation:', error);
      throw error;
    }
  };

  const getInstallationById = async (id: string) => {
    try {
      const response = await axiosInstance.get(`/api/installations/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching installation by ID:', error);
      throw error;
    }
  };

  const fetchAssignedTasks = async (params: {
    page?: number;
    limit?: number;
    type?: string;
    status?: string;
    userId?: string;
    search?: string;
  }) => {
    try {
      const { page = 1, limit = 10, type = '', status = '', userId, search } = params;
      const queryParams: Record<string, string | number> = {
        page,
        limit,
        type,
        status,
      };
      if (userId) {
        queryParams.userId = userId;
      }
      if (search != null && String(search).trim() !== '') {
        queryParams.search = String(search).trim();
      }
      const response = await axiosInstance.get('/api/installations/assigned-tasks', {
        params: queryParams
      });
      assignedTasksData.value = {
        assignedTasks: response.data.assignedTasks,
        totalCount: response.data.totalCount,
        page: response.data.page,
        limit: response.data.limit,
        totalPages: response.data.totalPages,
        filterCounts: response.data.filterCounts || {}
      };
    } catch (error) {
      console.error('Error fetching assigned tasks:', error);
      throw error;
    }
  };

  const assignInstallation = async (userId: string, installationId: string) => {
    try {
      const authStore = useAuthStore();
      if (!authStore.isAuthenticated) {
        throw new Error('User is not authenticated');
      }

      const response = await axiosInstance.post('/api/installations/assign', { userId, installationId }, {
        headers: {
          'Authorization': `Bearer ${authStore.token}`
        }
      });
      const newAssignedTask = response.data.assignedTask;
      const serverInstallation = response.data.updatedInstallation;

      // Update the installations data
      const localInstallation = installationsData.value.installations.find(
        installation => installation._id === installationId
      );
      if (localInstallation) {
        localInstallation.status = 'in-progress';
        if (serverInstallation?.installerId !== undefined) {
          localInstallation.installerId = serverInstallation.installerId;
        } else {
          localInstallation.installerId = userId;
        }
      }

      return newAssignedTask;
    } catch (error) {
      console.error('Error assigning installation:', error);
      throw error;
    }
  };

  const unassignInstallation = async (assignedTaskId: string) => {
    try {
      const authStore = useAuthStore();
      if (!authStore.isAuthenticated) {
        throw new Error('User is not authenticated');
      }

      const response = await axiosInstance.delete(`/api/installations/unassign/${assignedTaskId}`, {
        headers: {
          'Authorization': `Bearer ${authStore.token}`
        }
      });

      const { updatedInstallation, unassignedTaskId } = response.data;

      // Remove the task from assignedTasks
      assignedTasksData.value.assignedTasks = assignedTasksData.value.assignedTasks.filter(
        (task: AssignedTask) => task._id !== unassignedTaskId
      );

      // Update or add the installation to the installations list
      const existingIndex = installationsData.value.installations.findIndex(
        (installation) => installation._id === updatedInstallation._id
      );

      if (existingIndex !== -1) {
        installationsData.value.installations[existingIndex] = updatedInstallation;
      } else {
        // Add new installation to the list
        installationsData.value.installations.push(updatedInstallation);
      }

      // Optionally, you might want to update the total count
      // installationsData.value.totalCount = (installationsData.value.totalCount || 0) + 1;

      return response.data;
    } catch (error) {
      console.error('Error unassigning installation:', error);
      throw error;
    }
  };

  const approveOrRejectInstallation = async (
    installationId: string,
    action: 'approve' | 'reject',
    reason?: string,
    selectedService?: any
  ) => {
    try {
      const response = await axiosInstance.put(`/api/installations/${installationId}/${action}`, {
        reason,
        selectedService  // Make sure we're sending the selectedService in the request body
      });
      return response.data;
    } catch (error) {
      console.error(`Error ${action}ing installation:`, error);
      throw error;
    }
  };

  const fetchChecklistItems = async (params: FetchChecklistParams, signal?: AbortSignal) => {
    try {
      // Remove empty parameters
      const cleanParams = Object.fromEntries(
        Object.entries(params).filter(([_, value]) => value !== '' && value != null)
      );

      const response = await axiosInstance.get('/api/billing', {
        params: cleanParams,
        signal
      });
      checklistData.value = response.data;
    } catch (error) {
      if (axios.isCancel(error)) {
        console.log('fetchChecklistItems canceled:', error.message);
        return;
      }
      console.error('Error fetching checklist items:', error);
      throw error;
    }
  };

  /** Loads all Base services (filtered by branch and VISP access permissions when available). */
  const fetchServices = async (
    search: string = '',
    _page: number = 1,
    _limit: number = 1000,
    rs_IdOrBranchId?: string | { branchId?: string; branch_id?: string; rs_Id?: string; visp_ids?: number[] | string }
  ) => {
    try {
      servicesData.value.isLoading = true;

      const authStore = useAuthStore();
      const userBranch = authStore.user?.branch_id || authStore.user?.branch;
      const userBranchId = typeof userBranch === 'object' && userBranch !== null ? (userBranch as any)._id : userBranch;

      let targetBranchId: string | undefined = undefined;
      let targetRsId: string | undefined = undefined;
      let targetVispIds: any = undefined;

      if (typeof rs_IdOrBranchId === 'object' && rs_IdOrBranchId !== null) {
        targetBranchId = rs_IdOrBranchId.branchId || rs_IdOrBranchId.branch_id;
        targetRsId = rs_IdOrBranchId.rs_Id;
        targetVispIds = rs_IdOrBranchId.visp_ids;
      } else if (typeof rs_IdOrBranchId === 'string' && rs_IdOrBranchId.trim()) {
        const val = rs_IdOrBranchId.trim();
        // Check if val matches a known branch in branchesData
        const branchObj = branchesData.value?.branches?.find(
          (b: any) => String(b._id || b.id) === val
        );
        if (branchObj) {
          targetBranchId = val;
          if (branchObj.rs_Id) {
            const rs: any = branchObj.rs_Id;
            targetRsId = typeof rs === 'object' && rs !== null ? (rs._id || rs.id) : String(rs);
          }
          if (Array.isArray(branchObj.visps) && branchObj.visps.length > 0) {
            targetVispIds = branchObj.visps.map((v: any) => v.vispId).filter(Boolean);
          }
        } else {
          targetRsId = val;
        }
      } else {
        targetBranchId = userBranchId ? String(userBranchId) : undefined;
        targetRsId = authStore.user?.rs_Id ? String(authStore.user.rs_Id) : undefined;
      }

      // If branchId is known and vispIds is not yet determined, lookup from branchesData
      if (targetBranchId && !targetVispIds && branchesData.value?.branches?.length) {
        const branchObj = branchesData.value.branches.find(
          (b: any) => String(b._id || b.id) === String(targetBranchId)
        );
        if (branchObj) {
          if (!targetRsId && branchObj.rs_Id) {
            const rs: any = branchObj.rs_Id;
            targetRsId = typeof rs === 'object' && rs !== null ? (rs._id || rs.id) : String(rs);
          }
          if (Array.isArray(branchObj.visps) && branchObj.visps.length > 0) {
            targetVispIds = branchObj.visps.map((v: any) => v.vispId).filter(Boolean);
          }
        }
      }

      let vispParam: string | undefined = undefined;
      if (Array.isArray(targetVispIds) && targetVispIds.length > 0) {
        vispParam = targetVispIds.join(',');
      } else if (typeof targetVispIds === 'string' && targetVispIds.trim()) {
        vispParam = targetVispIds.trim();
      }

      let sourceParam: string | undefined = undefined;
      if (typeof rs_IdOrBranchId === 'object' && rs_IdOrBranchId !== null) {
        sourceParam = (rs_IdOrBranchId as any).source;
      }

      const response = await axiosInstance.get('/api/delta/services', {
        params: {
          q: search || undefined,
          rs_Id: targetRsId || undefined,
          branch_id: targetBranchId || undefined,
          visp_ids: vispParam || undefined,
          source: sourceParam || undefined,
          _t: Date.now()
        },
        ...skipPerm
      });

      const rawItems = response.data?.services || response.data?.data?.items || response.data?.data || [];
      const items = Array.isArray(rawItems) ? rawItems : [];

      const services = items.map((item: any) => ({
        Service_Id: String(item.Service_Id || item.sibServiceId || ''),
        ServiceName: item.ServiceName || item.sibServiceName || '',
        Description: item.Description || item.sibDescription || '',
        Price: String(item.Price ?? item.sibServicePrice ?? '0'),
        ServiceType: item.ServiceType || 'Base',
        ISEnable: item.ISEnable || 'Yes',
        UserChoosable: item.UserChoosable,
        ResellerChoosable: item.ResellerChoosable,
        Speed: item.Speed,
        ActiveDay: item.ActiveDay,
        ActiveMonth: item.ActiveMonth,
        ActiveYear: item.ActiveYear,
        VispAccess: item.VispAccess,
        vispNames: item.vispNames || (item.VispAccess === 'All' ? 'All' : undefined),
        durationInDays: item.durationInDays,
        qboItemId: item.qboItemId,
        qboItemName: item.qboItemName,
        qboItemPrice: item.qboItemPrice,
        status: item.status,
        _id: item._id || String(item.Service_Id || item.sibServiceId || '')
      }));

      servicesData.value = {
        ...servicesData.value,
        services,
        totalCount: services.length,
        page: 1,
        limit: services.length,
        isLoading: false,
        hasMore: false,
      };

      return {
        services,
        hasMore: false,
      };
    } catch (error) {
      console.error('Error fetching services from DeltaSIB:', error);
      servicesData.value.isLoading = false;
      throw error;
    }
  };

  const updateChecklistItemTotalPrice = (id: string, newPrice: number) => {
    const item = checklistData.value.items.find(item => item._id === id);
    if (item) {
      item.totalPrice = newPrice;
    }
  };

  const updateChecklistItemStatus = (id: string, newStatus: 'pending' | 'done' | 'others') => {
    const item = checklistData.value.items.find(item => item._id === id);
    if (item) {
      item.status = newStatus;
    }
  };


  const createPPoE = async (customerId: string) => {
    try {
      const authStore = useAuthStore();
      if (!authStore.isAuthenticated) {
        throw new Error('User is not authenticated');
      }

      const response = await axiosInstance.get(`/api/delta/ppoe/${customerId}`, {
        headers: {
          'Authorization': `Bearer ${authStore.token}`
        }
      });

      if (!response.data || response.data.error) {
        throw new Error(response.data?.error || 'Failed to create ppoe');
      }

      return response.data;
    } catch (error) {
      console.error('Error creating ppoe:', error);
      throw error;
    }
  };

  const fetchInvoices = async (search: string = '') => {
    try {
      const response = await axiosInstance.get('/api/invoices', {
        params: { search }
      });
      return response.data as InvoicesData;
    } catch (error) {
      console.error('Error fetching invoices:', error);
      throw error;
    }
  };

  const fetchCustomerInvoices = async (customerId: string) => {
    try {
      const response = await axiosInstance.get(`/api/invoices/customer/${customerId}`);
      invoicesData.value = response.data;
    } catch (error) {
      console.error('Error fetching customer invoices:', error);
      throw error;
    }
  };

  const fetchCustomerWithInvoices = async (customerId: string) => {
    try {
      const response = await axiosInstance.get(`/api/invoices/customer-invoices/${customerId}`);
      const { customer, invoices, summary, deltaSib, unappliedPayments, deltaSibError } = response.data;

      // Map DeltaSIB payload into legacy fields expected by the UI
      const activeService = deltaSib?.activeService ?? null;
      const creditInfo = deltaSib?.creditInfo ?? null;

      invoicesData.value = {
        invoices,
        summary,
        totalCount: invoices.length
      };

      return { customer, invoices, summary, creditInfo, activeService, deltaSib, unappliedPayments, deltaSibError };
    } catch (error) {
      console.error('Error fetching customer with invoices:', error);
      throw error;
    }
  };

  const fetchCustomerWithInvoicesLite = async (customerId: string) => {
    try {
      const response = await axiosInstance.get(`/api/invoices/customer-invoices-lite/${customerId}`);
      const { customer, invoices, summary, deltaSib, unappliedPayments } = response.data;

      invoicesData.value = {
        invoices,
        summary,
        totalCount: invoices.length
      };

      return { customer, invoices, summary, creditInfo: null, activeService: null, deltaSib, unappliedPayments };
    } catch (error) {
      console.error('Error fetching customer with invoices (lite):', error);
      throw error;
    }
  };

  const makeInvoicePayment = async ({ invoiceId, amount, customerId, lineItemId, depositToAccountId, referenceNo, memo, skipMongo }: InvoicePaymentParams) => {
    try {
      console.log('Making payment:', { invoiceId, amount, customerId, lineItemId, depositToAccountId, referenceNo, memo, skipMongo });
      const response = await axiosInstance.post('/api/invoices/payment', {
        invoiceId,
        amount,
        customerId,
        lineItemId,
        depositToAccountId,
        referenceNo,
        memo,
        skipMongo: !!skipMongo
      });
      return response.data;
    } catch (error) {
      console.error('Payment failed:', error);
      throw error;
    }
  };

  const searchInvoiceCustomers = async (
    search: string = '',
    fromQb: boolean = true,
    page: number = 1,
    signal?: AbortSignal
  ) => {
    try {
      const response = await axiosInstance.get('/api/invoices/search-customers', {
        params: {
          search,
          fromQb,
          page,
          limit: 20
        },
        signal
      });
      return response.data;
    } catch (error) {
      console.error('Error searching invoice customers:', error);
      throw error;
    }
  };

  const makeFullPayment = async (invoiceId: string, customerId: string, depositToAccountId: string) => {
    try {
      console.log('Processing full payment:', { invoiceId, customerId, depositToAccountId });
      const response = await axiosInstance.post('/api/invoices/payment/full', {
        invoiceId,
        customerId,
        depositToAccountId
      });

      // Refresh invoice data after successful payment
      await fetchCustomerInvoices(customerId);

      return response.data;
    } catch (error) {
      console.error('Full payment failed:', error);
      throw error;
    }
  };

  const syncBilling = async (
    items: Array<{ id: string, discount?: number, amount?: number }> | string[],
    location: string,
    branchAbbr?: string | null
  ) => {
    try {
      const authStore = useAuthStore();
      if (!authStore.isAuthenticated) {
        throw new Error('User is not authenticated');
      }

      // Support both old format (array of IDs) and new format (array of items with discount/amount)
      const requestBody = Array.isArray(items) && typeof items[0] === 'string'
        ? { ids: items, location, branchAbbr }
        : { items, location, branchAbbr };

      const response = await axiosInstance.post(`/api/billing/sync`, requestBody, {
        headers: {
          'Authorization': `Bearer ${authStore.token}`
        },
        timeout: 120000
      });

      // Update the local state immutably so table reactivity picks it up
      if (response.data.checklists && Array.isArray(response.data.checklists)) {
        const updatedMap = new Map(response.data.checklists.map((ci: any) => [ci._id, ci]));
        checklistData.value.items = checklistData.value.items.map((item) => {
          const updated = updatedMap.get(item._id);
          // Merge to preserve required fields and satisfy TS
          return updated ? ({ ...item, ...updated } as typeof item) : item;
        });
      }

      return response;
    } catch (error) {
      console.error('Error syncing credit check:', error);
      throw error;
    }
  };

  const recheckBilling = async (id: string) => {
    try {
      const authStore = useAuthStore();
      const response = await axiosInstance.post(`/api/billing/recheck/${id}`, {}, {
        headers: {
          'Authorization': `Bearer ${authStore.token}`
        },
        timeout: 60000
      });

      // Update the item in place so the table reflects the new status immediately
      if (response.data?.checklist) {
        checklistData.value.items = checklistData.value.items.map((item) =>
          item._id === id ? ({ ...item, ...response.data.checklist } as typeof item) : item
        );
      }

      return response.data;
    } catch (error) {
      console.error('Error rechecking credit check item:', error);
      throw error;
    }
  };

  const toggleManualStatus = async (id: string, isManual: boolean) => {
    try {
      const response = await axiosInstance.put(`/api/billing/${id}/manual`, { isManual });

      // Update the item in the store
      checklistData.value.items = checklistData.value.items.map(item =>
        item._id === id ? response.data.checklist : item
      );

      return response.data;
    } catch (error) {
      console.error('Error toggling manual status:', error);
      throw error;
    }
  };

  const toggleHideStatus = async (id: string) => {
    try {
      const response = await axiosInstance.put(`/api/billing/${id}/hide`);

      // Remove the item from the list if it's hidden, or refresh the list if unhidden
      if (response.data.checklist.isHidden) {
        checklistData.value.items = checklistData.value.items.filter(item => item._id !== id);
        checklistData.value.totalCount--;
      } else {
        // Refresh the entire list to show unhidden item in correct position
        await fetchChecklistItems({
          page: checklistData.value.page,
          limit: checklistData.value.limit,
          status: '', // Use current filters
          type: '',
        });
      }

      return response.data;
    } catch (error) {
      console.error('Error toggling hide status:', error);
      throw error;
    }
  };

  const updateChecklistInvoiceMemo = async (id: string, invoiceMemo: string) => {
    try {
      const authStore = useAuthStore();
      if (!authStore.isAuthenticated) {
        throw new Error('User is not authenticated');
      }

      const response = await axiosInstance.put(`/api/billing/${id}/memo`, {
        invoiceMemo: String(invoiceMemo || '').trim(),
      });

      const updated = response.data?.checklist;
      if (updated?._id) {
        const idx = checklistData.value.items.findIndex((item) => item._id === updated._id);
        if (idx !== -1) {
          checklistData.value.items[idx] = {
            ...checklistData.value.items[idx],
            ...updated,
          };
          checklistData.value.items = [...checklistData.value.items];
        }
      }

      return response.data;
    } catch (error) {
      console.error('Error updating invoice memo:', error);
      throw error;
    }
  };

  const loadNewBillingItems = async (options?: {
    date?: string
    rs_Id?: string
    radiusServerTag?: string
  }) => {
    try {
      const authStore = useAuthStore();
      if (!authStore.isAuthenticated) {
        throw new Error('User is not authenticated');
      }

      const requestBody: {
        date?: string
        rs_Id?: string
        radiusServerTag?: string
      } = {};
      if (typeof options === 'string') {
        // Backward compatible: old callers passed a date string
        requestBody.date = options;
      } else if (options) {
        if (options.date) requestBody.date = options.date;
        if (options.rs_Id) requestBody.rs_Id = options.rs_Id;
        if (options.radiusServerTag) requestBody.radiusServerTag = options.radiusServerTag;
      }

      const response = await axiosInstance.post('/api/billing/load-new', requestBody, {
        headers: {
          'Authorization': `Bearer ${authStore.token}`
        },
        timeout: 900000 // 15 minutes — matches server timeout for batches up to 500 items
      });

      return response.data;
    } catch (error) {
      console.error('Error loading new credit check items:', error);
      throw error;
    }
  };

  const retryChecklistItem = async (id: string) => {
    try {
      const authStore = useAuthStore();
      if (!authStore.isAuthenticated) {
        throw new Error('User is not authenticated');
      }

      const response = await axiosInstance.post(`/api/billing/retry/${id}`, {}, {
        headers: {
          'Authorization': `Bearer ${authStore.token}`
        }
      });

      // Update the item in the local store
      if (response.data.success && response.data.checklist) {
        const updatedItem = response.data.checklist;
        const index = checklistData.value.items.findIndex(item => item._id === updatedItem._id);
        if (index !== -1) {
          checklistData.value.items.splice(index, 1, updatedItem);
        }
      }

      return response.data;
    } catch (error) {
      console.error('Error retrying checklist item:', error);
      throw error;
    }
  };

  const retryAllChecklistItems = async (options?: { rs_Id?: string }) => {
    try {
      const authStore = useAuthStore();
      if (!authStore.isAuthenticated) {
        throw new Error('User is not authenticated');
      }

      const body: Record<string, string> = {};
      if (options?.rs_Id) {
        body.rs_Id = options.rs_Id;
      }

      const response = await axiosInstance.post('/api/billing/retry-all', body, {
        headers: {
          'Authorization': `Bearer ${authStore.token}`
        },
        timeout: 300000 // 5 minutes timeout for bulk operation
      });

      return response.data;
    } catch (error) {
      console.error('Error retrying all checklist items:', error);
      throw error;
    }
  };

  const fetchDepositAccounts = async (options?: { force?: boolean }) => {
    if (!options?.force && depositAccountsLoaded.value && depositAccounts.value.length > 0) {
      return depositAccounts.value;
    }

    if (depositAccountsLoading.value) {
      while (depositAccountsLoading.value) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      return depositAccounts.value;
    }

    try {
      depositAccountsLoading.value = true;
      const { can } = await import('@/core/permissions/can');
      const authStore = useAuthStore();
      // Only load deposit accounts when user may use payment screens or branch management
      const canAccess =
        can('page.receivePayment') ||
        can('page.hotspotPayments') ||
        can('page.branches') ||
        can('action.branch.create') ||
        can('action.branch.edit') ||
        authStore.user?.role === 'admin';

      if (!canAccess) {
        return depositAccounts.value;
      }

      const response = await axiosInstance.get('/api/invoices/deposit-accounts', skipPerm);
      const list = Array.isArray(response.data) ? (response.data as DepositAccount[]) : [];
      if (list.length > 0) {
        depositAccounts.value = list;
        depositAccountsLoaded.value = true;
        try {
          localStorage.setItem(DEPOSIT_ACCOUNTS_STORAGE_KEY, JSON.stringify(list));
        } catch (e) {
          console.warn('Failed to save deposit accounts to localStorage:', e);
        }
      }
      return depositAccounts.value;
    } catch (error: any) {
      if (error?.response?.status === 429) {
        return depositAccounts.value;
      }
      console.error('Error fetching deposit accounts:', error?.message || error);
      return depositAccounts.value;
    } finally {
      depositAccountsLoading.value = false;
    }
  };

  const qboConfigLoaded = ref(false);
  const qboConfigLoading = ref(false);

  const fetchQboData = async (options?: { radiusServerTag?: string | null; force?: boolean }) => {
    const authStore = useAuthStore();
    // Helper catalogs for drawers/forms — load if user may use customer/payment screens
    const needsCatalog =
      can('page.customersLink') ||
      can('page.customerForm') ||
      can('page.installationsList') ||
      can('page.receivePayment') ||
      can('page.billing') ||
      can('action.customer.edit') ||
      can('action.customer.completeQbRegistration') ||
      authStore.user?.role === 'admin';

    const shouldFetchLocations = needsCatalog;
    const shouldFetchClasses = needsCatalog;

    const requestedTag = String(
      options?.radiusServerTag ?? authStore.user?.radiusServerTag ?? ''
    )
      .trim()
      .toUpperCase() || null;

    const hasBaseData = qboVisps.value.length > 0 || qboCenters.value.length > 0;
    const classesReady = !shouldFetchClasses || qboClasses.value.length > 0;
    const locationsReady = !shouldFetchLocations || qboLocations.value.length > 0;
    const tagMatches = qboDataRadiusServerTag.value === requestedTag;

    if (
      !options?.force &&
      qboDataLoaded.value &&
      hasBaseData &&
      classesReady &&
      locationsReady &&
      tagMatches
    ) {
      return {
        classes: qboClasses.value,
        locations: qboLocations.value,
        centers: qboCenters.value,
        visps: qboVisps.value,
        supporters: qboSupporters.value,
        statuses: qboStatuses.value,
      };
    }

    if (qboDataLoading.value) {
      while (qboDataLoading.value) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
      if (!options?.force && qboDataRadiusServerTag.value === requestedTag) {
        return {
          classes: qboClasses.value,
          locations: qboLocations.value,
          centers: qboCenters.value,
          visps: qboVisps.value,
          supporters: qboSupporters.value,
          statuses: qboStatuses.value,
        };
      }
      // If forced or tag differs from what was just loaded, continue to fetch requested tag
    }

    try {
      qboDataLoading.value = true;

      const queryStr = requestedTag
        ? `?radiusServerTag=${encodeURIComponent(requestedTag)}`
        : '';

      const requests: Promise<any>[] = [
        axiosInstance.get(`/api/qbo-customer-links/centers${queryStr}`, skipPerm),
        axiosInstance.get(`/api/qbo-customer-links/visps${queryStr}`, skipPerm),
        axiosInstance.get(`/api/delta/supporters${queryStr}`, skipPerm),
        axiosInstance.get(`/api/delta/status-list${queryStr}`, skipPerm),
      ];

      if (shouldFetchClasses) {
        requests.push(axiosInstance.get('/api/invoices/qb-classes', skipPerm));
      }
      if (shouldFetchLocations) {
        requests.push(axiosInstance.get('/api/invoices/qb-locations', skipPerm));
      }

      const responses = await Promise.all(requests);

      const centersRes = responses[0];
      const vispsRes = responses[1];
      const supportersRes = responses[2];
      const statusesRes = responses[3];

      qboCenters.value = centersRes.data?.centers || (Array.isArray(centersRes.data) ? centersRes.data : []);
      qboVisps.value = vispsRes.data?.visps || (Array.isArray(vispsRes.data) ? vispsRes.data : []);
      qboSupporters.value = supportersRes.data?.supporters || (Array.isArray(supportersRes.data) ? supportersRes.data : []);
      qboStatuses.value = statusesRes.data?.statuses || (Array.isArray(statusesRes.data) ? statusesRes.data : []);

      let nextIdx = 4;
      if (shouldFetchClasses) {
        const classesRes = responses[nextIdx++];
        qboClasses.value = classesRes.data?.data?.classes || classesRes.data?.classes || [];
      } else {
        qboClasses.value = [];
      }
      if (shouldFetchLocations) {
        const locationsRes = responses[nextIdx++];
        qboLocations.value = locationsRes.data?.locations || [];
      } else {
        qboLocations.value = [];
      }

      qboDataLoaded.value = true;
      qboDataRadiusServerTag.value = requestedTag;

      return {
        classes: qboClasses.value,
        locations: qboLocations.value,
        centers: qboCenters.value,
        visps: qboVisps.value,
        supporters: qboSupporters.value,
        statuses: qboStatuses.value,
      };
    } catch (error: any) {
      if (requestedTag) {
        qboCenters.value = [];
        qboVisps.value = [];
        qboDataRadiusServerTag.value = null;
      }
      if (error?.response?.status === 429) {
        return { classes: [], locations: [], centers: [], visps: [], supporters: [], statuses: [] };
      }
      console.error('Error fetching QB data:', error?.message || error);
      return { classes: [], locations: [], centers: [], visps: [], supporters: [], statuses: [] };
    } finally {
      qboDataLoading.value = false;
    }
  };

  const fetchPayments = async (page: number = 1, limit: number = 10, search: string = '', customerId: string = '') => {
    try {
      const response = await axiosInstance.get('/api/invoices/payments', {
        params: {
          page,
          limit,
          search,
          customerId
        }
      });

      paymentsData.value = {
        payments: response.data.payments,
        totalCount: response.data.pagination.totalCount,
        page: response.data.pagination.page,
        limit: response.data.pagination.limit,
        totalPages: response.data.pagination.totalPages
      };

      return response.data;
    } catch (error) {
      console.error('Error fetching payments:', error);
      throw error;
    }
  };

  const fetchCustomersDropdown = async (search: string = '', page: number = 1, limit: number = 20) => {
    try {
      const response = await axiosInstance.get('/api/invoices/customers-dropdown', {
        params: { search, page, limit }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching customers dropdown:', error);
      throw error;
    }
  };

  const fetchInvoiceByNumber = async (invoiceNumber: string, options?: { fresh?: boolean }) => {
    try {
      console.log('Fetching invoice number:', invoiceNumber);
      // URL-encode the invoice number to handle slashes and special characters (e.g., "AWB/178937")
      const encodedNumber = encodeURIComponent(invoiceNumber);
      const response = await axiosInstance.get(`/api/invoices/${encodedNumber}`, {
        params: options?.fresh ? { _t: Date.now() } : undefined
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching invoice:', error);
      throw error;
    }
  };

  const fetchLastPaymentNumber = async (branchAbbr: string) => {
    try {
      const response = await axiosInstance.get(`/api/invoices/last-payment-number/${branchAbbr}`);
      return response.data.lastNumber || 0;
    } catch (error) {
      console.error('Error fetching last payment number:', error);
      throw error;
    }
  };

  const fetchTransactions = async (
    customerId: string = '',
    type: 'all' | 'invoice' | 'payment' = 'all',
    page: number = 1,
    limit: number = 10,
    from: string = '',
    to: string = '',
    centerId: string = '',
    rs_Id: string = ''
  ) => {
    try {
      const params: Record<string, string | number> = {
        customerId,
        type,
        page,
        limit,
        from,
        to
      };
      if (centerId) params.centerId = centerId;
      if (rs_Id) params.rs_Id = rs_Id;

      const response = await axiosInstance.get('/api/invoices/transactions', { params });
      transactionsData.value = response.data;
      return response.data;
    } catch (error) {
      console.error('Error fetching transactions:', error);
      throw error;
    }
  };

  const getServiceDiscounts = async (params: {
    page: number
    limit: number
    search?: string
    status?: string
    rs_Id?: string
    centerId?: string
  }) => {
    const response = await axiosInstance.get('/api/service-discounts', { params });
    return response.data;
  };

  const updateServiceDiscountStatus = async (id: string, status: 'approved' | 'pending' | 'rejected') => {
    const response = await axiosInstance.put(`/api/service-discounts/${id}/status`, { status });
    return response.data;
  };

  const syncServiceDiscounts = async () => {
    const response = await axiosInstance.post('/api/service-discounts/sync', {});
    return response.data;
  };

  const getServiceDiscountsSyncStatus = async () => {
    const response = await axiosInstance.get('/api/service-discounts/sync-status');
    return response.data;
  };

  return {
    branchesData,
    fetchBranches,
    addBranch,
    updateBranch,
    getBranchById,
    deleteBranch,
    equipmentData,
    fetchEquipment,
    addEquipment,
    updateEquipment,
    getEquipmentById,
    deleteEquipment,
    token,
    customersData,
    fetchCustomers,
    addCustomer,
    updateCustomer,
    deleteCustomer,
    updateCustomerDevices,
    getCustomerById,
    fetchLastContractId,
    fetchQboCustomFields,
    fetchResellers,
    addReseller,
    updateReseller,
    deleteReseller,
    resellers,
    resellersData,
    resellersLoading,
    translateCustomerName,
    makeSecureRequest,
    installationsData,
    fetchInstallations,
    addInstallation,
    updateInstallation,
    deleteInstallation,
    getInstallationById,
    assignedTasksData,
    fetchAssignedTasks,
    assignInstallation,
    unassignInstallation,
    approveOrRejectInstallation,
    checklistData,
    fetchChecklistItems,
    servicesData,
    fetchServices,
    updateChecklistItemTotalPrice,
    updateChecklistItemStatus,
    createPPoE,
    invoicesData,
    fetchInvoices,
    fetchCustomerInvoices,
    fetchCustomerWithInvoices,
    fetchCustomerWithInvoicesLite,
    makeInvoicePayment,
    searchInvoiceCustomers,
    makeFullPayment,
    syncBilling,
    recheckBilling,
    loadNewBillingItems,
    retryChecklistItem,
    retryAllChecklistItems,
    toggleManualStatus,
    toggleHideStatus,
    updateChecklistInvoiceMemo,
    depositAccounts,
    depositAccountsLoading,
    depositAccountsLoaded,
    fetchDepositAccounts,
    qboClasses,
    qboLocations,
    qboCenters,
    qboVisps,
    qboSupporters,
    qboStatuses,
    qboCurrencies,
    qboPaymentMethods,
    qboPaymentTerms,
    qboDeliveryOptions,
    qboLanguages,
    qboItems,
    fetchQboItems,
    qboDataLoading,
    qboDataLoaded,
    qboConfigLoaded,
    qboConfigLoading,
    fetchQboData,
    fetchQboConfigData,
    clearQboDataCache,
    paymentsData,
    fetchPayments,
    fetchCustomersDropdown,
    fetchInvoiceByNumber,
    fetchLastPaymentNumber,
    transactionsData,
    fetchTransactions,
    getServiceDiscounts,
    updateServiceDiscountStatus,
    syncServiceDiscounts,
    getServiceDiscountsSyncStatus
  }
})
